import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types/database';

let supabaseInstance: SupabaseClient<Database> | null = null;

/**
 * No-op lock: skips Navigator LockManager to avoid "Acquiring lock timed out waiting 10000ms".
 * The default Lock API can block when multiple tabs/operations contend for the same lock.
 * Trade-off: minimal cross-tab sync; acceptable for most SPA use cases.
 */
const noOpLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => fn();

export const initSupabase = (supabaseUrl: string, supabaseAnonKey: string) => {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  supabaseInstance = createClient<Database>(
    supabaseUrl,
    supabaseAnonKey,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: globalThis.localStorage,
        storageKey: 'lawgentia-auth-token',
        flowType: 'pkce',
        lock: noOpLock,
      }
    }
  );

  return supabaseInstance;
};

export const getSupabase = (): SupabaseClient<Database> => {
  if (!supabaseInstance) {
    throw new Error('Supabase not initialized. Call initSupabase first.');
  }
  return supabaseInstance;
};

// Mantener export para compatibilidad (será un getter)
export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get: ( _target, prop) => {
    const instance = getSupabase();
    return (instance as any)[prop];
  }
});

// Helper to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper to check if user is authenticated
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

// Helper to get current session with detailed info
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    throw error;
  }
  if (!session) {
    console.warn('No active session found');
    return null;
  }
  console.log('Current session:', {
    userId: session.user.id,
    email: session.user.email,
    expiresAt: session.expires_at,
  });
  return session;
};

